import { Box, CircularProgress, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useSnackbar } from '@/shared/components/notifications';
import { CreateAlertFormValues } from '../types';
import { AlertListItem } from '../types';
import { getAlertById, updateAlert } from '../services/alertsService';
import { AlertForm } from './AlertForm';

function mapAlertToFormValues(alert: AlertListItem): CreateAlertFormValues {
  return {
    address: alert.address,
    latitude: alert.latitude,
    longitude: alert.longitude,
    distanceEnabled: alert.maxDistance !== null,
    maxDistance: alert.maxDistance ?? 25,
    materialId: alert.materialId,
    materialCategory: alert.materialCategory || '',
    materialSubcategory: alert.materialSubcategory || '',
    condition: alert.condition ?? '',
    availabilityEnabled: alert.availabilityEnabled,
    availableFrom: alert.startDate ? new Date(alert.startDate) : null,
    availableTo: alert.endDate ? new Date(alert.endDate) : null,
  };
}

export function EditAlertPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();

  const [alert, setAlert] = useState<AlertListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) {
      setError(true);
      setLoading(false);
      return;
    }

    const fetchAlert = async () => {
      try {
        setLoading(true);
        const data = await getAlertById(id);
        setAlert(data);
      } catch (err) {
        console.error('Error fetching alert:', err);
        setError(true);
        showError(t('alerts.edit.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchAlert();
  }, [id, showError, t]);

  const handleSubmit = async (data: CreateAlertFormValues) => {
    if (!id) return;
    try {
      await updateAlert(id, data);
      showSuccess(t('alerts.edit.successMessage'));
      navigate('/alerts');
    } catch (err) {
      console.error('Error updating alert:', err);
      showError(t('alerts.edit.errorMessage'));
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
        data-testid="edit-alert-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !alert) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2,
        }}
        data-testid="edit-alert-error"
      >
        <Typography variant="h6" color="error">
          {t('alerts.edit.loadError')}
        </Typography>
      </Box>
    );
  }

  return (
    <AlertForm
      defaultValues={mapAlertToFormValues(alert)}
      onSubmit={handleSubmit}
      title={t('alerts.edit.title')}
      submitLabel={t('alerts.edit.submitButton')}
      submittingLabel={t('alerts.edit.submitting')}
      testIdPrefix="edit-alert"
    />
  );
}
